// ============================================================================
// Widget — Active Litigation Aging (by age × case type) + chart-click drill
// Stacked bars: one stack per age bucket, segments coloured by case type.
// Overlay: total outstanding (₹) per bucket on a secondary axis.
// Click a bar segment → DrillDrawer opens with the matching cases as a table.
// ============================================================================
import React, { useCallback, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { SyutGrid, type SyutGridColumn } from 'components/ui';
import WidgetShell from './WidgetShell';
import DrillDrawer from '../DrillDrawer';
import {
  mockLitigationActive,
  LITIGATION_CASE_TYPES,
  filterActiveLitigation,
  fmtAmount,
  fmtCount,
  type ActiveLitigationCase,
} from '../executiveMockData';

const LitigationActiveAgingWidget: React.FC = () => {
  const buckets = mockLitigationActive;
  const labels  = buckets.map(b => b.ageLabel);

  // ---- Drill state ----
  const [drill, setDrill] = useState<{ ageBucket?: string; caseType?: string; colour?: string } | null>(null);
  const drillRows: ActiveLitigationCase[] = useMemo(
    () => drill ? filterActiveLitigation(drill.ageBucket, drill.caseType) : [],
    [drill],
  );
  const drillOs = drillRows.reduce((s, r) => s + r.outstanding, 0);

  // ECharts emits the params object with seriesName=caseType, name=ageBucket.
  const handleChartClick = useCallback((params: any) => {
    if (!params || !params.seriesName || params.seriesType !== 'bar') return;
    const ct = LITIGATION_CASE_TYPES.find(c => c.label === params.seriesName);
    setDrill({ ageBucket: params.name, caseType: params.seriesName, colour: ct?.colour });
  }, []);

  // ---- Chart series ----
  const series: any[] = LITIGATION_CASE_TYPES.map(ct => ({
    name: ct.label,
    type: 'bar',
    stack: 'count',
    yAxisIndex: 0,
    barWidth: 28,
    cursor: 'pointer',
    itemStyle: { color: ct.colour, borderRadius: [0, 0, 0, 0] },
    emphasis: { focus: 'series', itemStyle: { opacity: 0.85 } },
    data: buckets.map(b => {
      const m = b.byType.find(t => t.caseType === ct.label);
      return m ? m.caseCount : 0;
    }),
  }));
  series[series.length - 1].itemStyle = {
    ...series[series.length - 1].itemStyle,
    borderRadius: [3, 3, 0, 0],
  };

  const osTotalsPerBucket = buckets.map(b =>
    b.byType.reduce((acc, t) => acc + t.outstanding, 0),
  );
  series.push({
    name: 'Outstanding',
    type: 'line',
    yAxisIndex: 1,
    smooth: true,
    symbol: 'circle',
    symbolSize: 7,
    silent: true,                                  // line is informational; only bars drill
    lineStyle: { width: 2, color: '#d97706' },
    itemStyle: { color: '#d97706' },
    z: 5,
    data: osTotalsPerBucket,
  });

  // ---- Subtitle totals + weighted-average age ----
  const totalCasesPerBucket = buckets.map(b =>
    b.byType.reduce((sum, t) => sum + t.caseCount, 0));
  const totalCases = totalCasesPerBucket.reduce((a, b) => a + b, 0);
  const totalOs    = osTotalsPerBucket.reduce((a, b) => a + b, 0);
  const midpoints  = [15, 60, 135, 273, 500];
  const avgAge     = Math.round(
    totalCasesPerBucket.reduce((sum, c, i) => sum + c * midpoints[i], 0) / Math.max(totalCases, 1),
  );

  const option = {
    grid: { left: 44, right: 56, top: 28, bottom: 28 },
    legend: { show: true, top: 0, left: 0, fontSize: 10, itemHeight: 8, itemWidth: 12 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any[]) => {
        const idx = params[0].dataIndex;
        const b   = buckets[idx];
        const lines = b.byType
          .filter(t => t.caseCount > 0)
          .map(t => {
            const colour = LITIGATION_CASE_TYPES.find(c => c.label === t.caseType)?.colour ?? '#94a3b8';
            return `<span style="display:inline-block;width:8px;height:8px;background:${colour};margin-right:6px;border-radius:2px"></span>` +
                   `${t.caseType}: ${fmtCount(t.caseCount)} (${fmtAmount(t.outstanding)})`;
          }).join('<br/>');
        const totC = b.byType.reduce((s, t) => s + t.caseCount, 0);
        const totO = b.byType.reduce((s, t) => s + t.outstanding, 0);
        return `<strong>${b.ageLabel} days since opening</strong><br/>${lines}<br/>` +
               `<strong>Total: ${fmtCount(totC)} cases · ${fmtAmount(totO)}</strong><br/>` +
               `<em style="color:#64748b;font-size:11px">Click a coloured segment to drill in</em>`;
      },
    },
    xAxis: { type: 'category', data: labels, axisTick: { show: false }, axisLabel: { fontSize: 11 } },
    yAxis: [
      {
        type: 'value', name: 'Cases',
        nameTextStyle: { fontSize: 10, color: '#64748b' },
        axisLabel: { fontSize: 10 },
        splitLine: { lineStyle: { color: '#e5e7eb', type: 'dashed' } },
      },
      {
        type: 'value', name: '₹ Cr',
        nameTextStyle: { fontSize: 10, color: '#64748b' },
        axisLabel: { fontSize: 10, formatter: (v: number) => (v / 1_00_00_000).toFixed(1) },
        splitLine: { show: false },
      },
    ],
    series,
  };

  // ---- Drill table columns ----
  const columns: SyutGridColumn<ActiveLitigationCase>[] = [
    { id: 'caseCode',    header: 'Case #',     accessorKey: 'caseCode',     size: 140, enableSorting: true,
      cell: ({ row }) => <strong>{row.original.caseCode}</strong> },
    { id: 'caseType',    header: 'Type',       accessorKey: 'caseType',     size: 130, enableSorting: true },
    { id: 'borrower',    header: 'Borrower',   accessorKey: 'borrowerName', size: 170, enableSorting: true },
    { id: 'branchCode',  header: 'Branch',     accessorKey: 'branchCode',   size: 110, enableSorting: true },
    { id: 'stage',       header: 'Stage',      accessorKey: 'currentStage', size: 170, enableSorting: true },
    { id: 'ageDays',     header: 'Age',        accessorKey: 'ageDays',      size:  80, enableSorting: true,
      cell: ({ row }) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.original.ageDays}d</span> },
    { id: 'nextHearing', header: 'Next Hearing', accessorKey: 'nextHearing', size: 120, enableSorting: true },
    { id: 'advocate',    header: 'Advocate',   accessorKey: 'assignedAdvocate', size: 160, enableSorting: true },
    { id: 'os',          header: 'Outstanding', accessorKey: 'outstanding', size: 130, enableSorting: true,
      cell: ({ row }) => <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtAmount(row.original.outstanding)}</span> },
  ];

  // ---- Drill drawer title + chips ----
  const drillTitle = drill
    ? `${drill.caseType} cases · ${drill.ageBucket} days`
    : '';
  const drillSubtitle = drill
    ? `${fmtCount(drillRows.length)} case${drillRows.length === 1 ? '' : 's'} · ${fmtAmount(drillOs)} outstanding`
    : '';

  return (
    <>
      <WidgetShell
        title="Active Litigation — Age × Type"
        subtitle={`${fmtCount(totalCases)} cases · ${fmtAmount(totalOs)} OS · avg age ~${avgAge} days`}
      >
        <ReactECharts
          option={option}
          style={{ height: 240, width: '100%' }}
          onEvents={{ click: handleChartClick }}
        />
      </WidgetShell>

      <DrillDrawer
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drillTitle}
        subtitle={drillSubtitle}
        filterChips={drill ? [
          { label: `Status: Active`,                        colour: '#16a34a' },
          { label: `Age: ${drill.ageBucket} days`,          colour: '#94a3b8' },
          { label: `Type: ${drill.caseType}`,               colour: drill.colour },
        ] : []}
      >
        <SyutGrid
          data={drillRows}
          columns={columns}
          enableSorting
          enablePagination
          pageSize={10}
          emptyMessage="No cases match this slice."
        />
      </DrillDrawer>
    </>
  );
};

export default LitigationActiveAgingWidget;
